import React from "react";
import { useNavigate } from "react-router-dom";
import { AccountService } from "../../../DataService/AccountService";
import { useUser } from "../../../DataService/UserContext";
import "../MemberDashboard.css";
export const DashboardProfile = () => {

  const { user, signOut } = useUser();
  const navigate = useNavigate();
  const [busyAction, setBusyAction] = React.useState<"cancel" | "download" | null>(null);
  const [membershipNotice, setMembershipNotice] = React.useState<string | null>(null);

  if (!user) return null;

  const membershipActive = Boolean(
    user.membershipExpiresAt &&
    new Date(user.membershipExpiresAt) > new Date()
  );
  const membershipLabel = user.membershipPlanKey === "premium"
    ? "GROW Premium"
    : user.membershipPlanKey === "basic"
      ? "GROW Basic"
      : membershipActive
        ? "Aktivt medlemskap"
        : "Inget aktivt medlemskap";
  const canUpgrade = membershipActive && user.membershipPlanKey === "basic";

  const handleCancelMembership = async () => {
    setBusyAction("cancel");
    try {
      const result = await AccountService.cancelMembership();
      const periodEnd = result.currentPeriodEnd ? new Date(result.currentPeriodEnd).toLocaleDateString() : null;
      setMembershipNotice(periodEnd ? `${result.message} Tillgången fortsätter till ${periodEnd}.` : result.message);
    } catch (error) {
      console.error(error);
      setMembershipNotice("Kunde inte avsluta medlemskapet just nu.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleDowngradeToBasic = async () => {
    setBusyAction("cancel");
    try {
      const result = await AccountService.cancelMembership();
      const periodEnd = result.currentPeriodEnd ? new Date(result.currentPeriodEnd).toLocaleDateString() : null;
      setMembershipNotice(periodEnd ? `${result.message} Tillgången fortsätter till ${periodEnd}. Du kan sedan välja Basic igen.` : `${result.message} Du kan sedan välja Basic igen.`);
      navigate("/membership");
    } catch (error) {
      console.error(error);
      setMembershipNotice("Kunde inte starta nedgradering just nu.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownloadData = async () => {
    setBusyAction("download");
    try {
      const blob = await AccountService.downloadMyData();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sandrasportal-my-data.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      window.alert("Kunde inte ladda ner dina uppgifter just nu.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="dashboard-section">

      <div className="dashboard-header">
        <h1>Ditt konto</h1>
        <p>Hantera dina uppgifter och medlemskap</p>
      </div>

      {/* USER INFO */}

      <div className="dashboard-card">

        <h3>Dina uppgifter</h3>

        <div className="dashboard-profile-grid">

          <div className="dashboard-profile-field">
            <span>Namn</span>
            <p>{[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email.split("@")[0]}</p>
          </div>

          <div className="dashboard-profile-field">
            <span>E-post</span>
            <p>{user.email}</p>
          </div>

          <div className="dashboard-profile-field">
            <span>Telefon</span>
            <p>{user.phone ?? "Saknas"}</p>
          </div>

          <div className="dashboard-profile-field">
            <span>Adress</span>
            <p>{user.address ? `${user.address}, ${user.postalCode ?? ""} ${user.city ?? ""}`.trim() : "Saknas"}</p>
          </div>

        </div>

      </div>

      {/* MEMBERSHIP */}

      <div className="dashboard-card">

        <h3>Medlemskap</h3>

        {membershipActive ? (

          <div className="dashboard-membership">

            <p className="membership-active">
              Aktivt medlemskap
            </p>

            <p>
              Nuvarande nivå: {membershipLabel}
            </p>

            <p className="dashboard-profile-note">Du har full åtkomst till medlemsmaterial och resurser.</p>
            <p className="dashboard-profile-note">Säger du upp nu behåller du åtkomst till periodslutet.</p>

            <p>
              Giltigt till:
              {" "}
              {new Date(
                user.membershipExpiresAt ?? ''
              ).toLocaleDateString()}
            </p>

          </div>

        ) : (

          <div className="dashboard-membership">

            <p className="membership-inactive">
              Inget aktivt medlemskap
            </p>

            <p className="dashboard-profile-note">Förnya medlemskapet för att återfå tillgång till resurser.</p>

          </div>

        )}

        {membershipNotice && (
          <p className="dashboard-profile-note" style={{ marginTop: 12 }}>
            {membershipNotice}
          </p>
        )}

        {canUpgrade && (
          <div style={{ marginTop: 16 }}>
            <button
              className="dashboard-logout-btn"
              onClick={() => navigate("/membership")}
              disabled={busyAction !== null}
            >
              Uppgradera till Premium
            </button>
          </div>
        )}

        {membershipActive && (
          <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              className="dashboard-logout-btn"
              onClick={handleCancelMembership}
              disabled={busyAction !== null}
            >
              {busyAction === "cancel" ? "Avslutar..." : "Säg upp medlemskap"}
            </button>

            {user.membershipPlanKey === "premium" && (
              <button
                className="dashboard-logout-btn"
                onClick={handleDowngradeToBasic}
                disabled={busyAction !== null}
              >
                {busyAction === "cancel" ? "Byter..." : "Byt till Basic"}
              </button>
            )}
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <button
            className="dashboard-logout-btn"
            onClick={handleDownloadData}
            disabled={busyAction !== null}
          >
            {busyAction === "download" ? "Laddar ner..." : "Ladda ner mina uppgifter"}
          </button>
        </div>

      </div>

      {/* ACCOUNT ACTIONS */}

      <div className="dashboard-card">

        <h3>Konto</h3>

        <button
          className="dashboard-logout-btn"
          onClick={signOut}
        >
          Logga ut
        </button>

      </div>

    </div>
  );
};
